import { ESTIMATE_INVOICE_JOB_ID } from '../invoice/invoiceSnapshot';
import { formatEstimateNumberKnownYear } from './formatEstimateNumber';

// Re-export for callers that import from here
export { ESTIMATE_INVOICE_JOB_ID };

export function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/**
 * The invoice total a billing fraction produces — the ONE formula
 * buildConvertInvoiceSnapshotFromEstimate uses, exported so the payment plan
 * can show exactly the amount the snapshot will land on (discount and tax are
 * both scaled by the factor, each rounded, then the total rounded).
 */
export function convertPortionTotal({ subtotal, discount = 0, taxAmount = 0, factor }) {
  const scaledDiscount = round2((Number(discount) || 0) * factor);
  const scaledTax = round2((Number(taxAmount) || 0) * factor);
  return round2(Number(subtotal) * factor - scaledDiscount + scaledTax);
}

/**
 * Build snapshot + line items for converting an estimate to a draft invoice (partial % supported).
 *
 * Line items are written UNSCALED: qty × price === total (per line, always).
 * The partial factor is expressed only at the invoice level via:
 *   snapshot.invoice.subtotal = full unscaled sum
 *   snapshot.invoice.total    = subtotal * factor
 *   snapshot.partial_adjustment (present only when factor < 1.0)
 *
 * @param {object} options
 * @param {object} options.estimate          - Estimate row
 * @param {object} options.customer          - Customer row
 * @param {Array}  options.items             - Estimate line items
 * @param {number} options.factor            - Billing fraction (0 < factor <= 1)
 * @param {string} options.today             - YYYY-MM-DD in tenant timezone
 * @param {string} [options.previousInvoiceNumber] - Invoice number(s) already billed, comma-joined (the FINAL payment only)
 * @param {number} [options.exactTotal] - Payment plan: the invoice total this slot MUST land on, to the cent (the
 *   final payment's exact remainder). The cents the factor math is off by are absorbed in `partial_adjustment`,
 *   never in a percent.
 * @param {object} [options.plan] - Payment plan facts ({ sequence, count, percent, estimateTotal,
 *   previouslyInvoiced, remainingAfter, isRest }); written to `snapshot.payment_plan` when count > 1.
 */
export function buildConvertInvoiceSnapshotFromEstimate({
  estimate,
  customer,
  items,
  factor,
  today,
  previousInvoiceNumber,
  exactTotal,
  plan,
}) {
  // Line items are UNSCALED — invariant: qty * price === total for every item.
  const snapshotItems = items.map((item) => {
    const qty = Number(item.quantity) || 1;
    const price = Number(item.unit_price) || 0;
    return {
      name: item.item_name || item.description || 'Line Item',
      qty,
      price,
      total: round2(qty * price),
      type: 'service',
    };
  });

  // Subtotal = full unscaled sum of all line item totals.
  const subtotal = round2(snapshotItems.reduce((s, x) => s + x.total, 0));
  // Discount + tax are BOTH scaled by factor so a partial invoice carries
  // its proportional share. Without scaling the discount, the deduction
  // line's math would not balance against subtotal/credit/total on the PDF
  // when the estimate has a non-zero discount.
  const discount = round2((Number(estimate.discount) || 0) * factor);
  const taxAmount = round2((Number(estimate.tax_amount) || 0) * factor);

  // Invoice total computed from the scaled bases so subtotal - deduction
  // - credit + tax === total holds visually on the PDF/preview.
  const naturalTotal = round2(subtotal * factor - discount + taxAmount);

  // partial_adjustment: shown between subtotal and total on invoices where
  // factor < 1. The deduction is just the unscaled-base deferred portion;
  // discount/tax are rendered on their own rows, not folded into this.
  const isPartial = factor < 1.0 - 0.0001;
  // Exact-remainder slot: land on `exactTotal` to the cent; the difference rides in the deduction.
  const hasExact = isPartial && Number.isFinite(Number(exactTotal));
  const total = hasExact ? round2(exactTotal) : naturalTotal;
  const exactDiffCents = hasExact ? Math.round((naturalTotal - total) * 100) : 0;
  let partialAdjustment;
  if (isPartial) {
    const deductionCents = Math.max(0, Math.round(subtotal * (1 - factor) * 100) + exactDiffCents);
    partialAdjustment = {
      i18nKey: previousInvoiceNumber
        ? 'invoice.partial.previouslyInvoiced'
        : 'invoice.partial.balanceRetained',
      ...(previousInvoiceNumber
        ? { i18nParams: { invoiceNumber: previousInvoiceNumber } }
        : {}),
      amount_cents: deductionCents,
    };
  }

  const snapshot = {
    version: 1,
    invoice_origin: 'estimate',
    source_estimate_number: estimate.estimate_number == null
      ? null
      : formatEstimateNumberKnownYear(estimate.estimate_number, estimate.estimate_number_year),
    created_at: new Date().toISOString(),
    invoice: {
      customer_id: estimate.customer_id,
      date_from: today,
      date_to: today,
      subtotal,
      credit_amount: discount,
      credit_description: discount > 0 ? 'Discount (from estimate)' : '',
      tax_amount: taxAmount,
      tax_rate: Number(estimate.tax_rate) || 0,
      tax_state: '',
      notes: estimate.customer_notes || '',
      balance_due: total,
      total,
      status: 'draft',
    },
    customer: {
      id: customer.id,
      full_name: customer.full_name || '',
      billing_address: customer.billing_address || '',
      service_address: customer.service_address || '',
      phone: customer.phone || '',
      email: customer.email || '',
    },
    jobs: [{
      id: ESTIMATE_INVOICE_JOB_ID,
      date: today,
      total: subtotal,
      service_address: null,
      items: snapshotItems,
    }],
    ...(partialAdjustment ? { partial_adjustment: partialAdjustment } : {}),
    ...(plan && Number(plan.count) > 1 ? {
      payment_plan: {
        sequence: plan.sequence,
        count: plan.count,
        percent: plan.percent,
        is_rest: Boolean(plan.isRest),
        estimate_total: round2(plan.estimateTotal),
        previously_invoiced: round2(plan.previouslyInvoiced),
        this_invoice: total,
        remaining_after: round2(plan.remainingAfter),
      },
    } : {}),
  };

  return { snapshot, total, subtotal, discount, taxAmount };
}
